<script lang="ts">
  import LegalLayout from "./LegalLayout.svelte";
</script>

<LegalLayout title="Terms of Service" effective="27 May 2026">
  <p>
    These Terms of Service ("Terms") govern your access to and use of Optio, a desktop AI
    orchestration application and its related cloud services (together, the "Service"). By creating
    an account, subscribing to a paid plan, or otherwise using the Service, you agree to these
    Terms. If you do not agree, do not use the Service.
  </p>

  <h2>1. The Service</h2>
  <p>
    Optio is a desktop agent that connects to your servers, runs tools, and manages context on your
    behalf. Some capabilities (such as remote control, memory, and advanced workflows) require a
    paid subscription. We may update, add, or remove features over time.
  </p>

  <h2>2. Eligibility and accounts</h2>
  <p>
    You must be able to form a binding contract to use the Service. You are responsible for the
    activity that occurs under your account and for keeping your credentials secure. Notify us
    promptly of any unauthorized use.
  </p>

  <h2>3. Orders, billing, and the Merchant of Record</h2>
  <p>
    Our order process and payments are handled by our reseller and Merchant of Record,
    <a href="https://www.paddle.com" target="_blank" rel="noopener noreferrer">Paddle.com</a>.
    Paddle is the seller of record for orders placed through the Service: it processes payments,
    handles billing, and is responsible for collecting and remitting applicable taxes. Your purchase
    is therefore also subject to Paddle's Buyer Terms and Privacy Policy.
  </p>
  <p>
    Paid plans are sold on a recurring subscription basis (monthly or yearly) and renew
    automatically at the end of each billing period until cancelled. Prices are shown at checkout
    and may be exclusive of taxes, which are calculated and displayed by Paddle. We may change
    prices; changes apply to billing periods that begin after we give notice.
  </p>

  <h2>4. Cancellations and refunds</h2>
  <p>
    You may cancel your subscription at any time to stop future renewals. Refunds are governed by
    our
    <a href="/refund">Refund Policy</a>, which forms part of these Terms.
  </p>

  <h2>5. Acceptable use</h2>
  <p>You agree not to:</p>
  <ul>
    <li>use the Service to break the law or infringe the rights of others;</li>
    <li>access systems or data you are not authorized to access;</li>
    <li>attempt to disrupt, reverse-engineer, or circumvent the security of the Service; or</li>
    <li>resell or sublicense the Service without our written permission.</li>
  </ul>
  <p>
    You are solely responsible for the servers you connect to and the actions you instruct the agent
    to perform.
  </p>

  <h2>6. Intellectual property</h2>
  <p>
    The Service, including its software and content, is owned by Optioway and its licensors and is
    protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable
    licence to use the Service in accordance with these Terms. You retain ownership of the data and
    content you provide.
  </p>

  <h2>7. Disclaimers</h2>
  <p>
    The Service is provided "as is" and "as available" without warranties of any kind, whether
    express or implied, to the maximum extent permitted by law. We do not warrant that the Service
    will be uninterrupted, error-free, or secure. AI-generated output may be inaccurate; you are
    responsible for reviewing it before relying on it.
  </p>

  <h2>8. Limitation of liability</h2>
  <p>
    To the maximum extent permitted by law, Optioway will not be liable for any indirect, incidental,
    special, consequential, or punitive damages, or for any loss of data, profits, or revenue. Our
    total liability arising out of or relating to the Service will not exceed the amount you paid
    for the Service in the twelve months before the event giving rise to the claim.
  </p>

  <h2>9. Termination</h2>
  <p>
    You may stop using the Service at any time. We may suspend or terminate your access if you
    breach these Terms or if we are required to do so by law. On termination, your right to use the
    Service ends; sections that by their nature should survive will continue to apply.
  </p>

  <h2>10. Changes to these Terms</h2>
  <p>
    We may update these Terms from time to time. When we make material changes, we will update the
    "Last updated" date above and, where appropriate, notify you. Your continued use of the Service
    after changes take effect constitutes acceptance of the updated Terms.
  </p>

  <h2>11. Governing law</h2>
  <p>
    These Terms are governed by the laws of the Kingdom of Saudi Arabia, without regard to its
    conflict-of-law rules. The courts of the Kingdom of Saudi Arabia have exclusive jurisdiction
    over any dispute arising out of or relating to these Terms or the Service.
  </p>

  <h2>12. Contact</h2>
  <p>
    Questions about these Terms can be sent to
    <a href="mailto:info@optioway.com">info@optioway.com</a>.
  </p>
</LegalLayout>
